import React, { useEffect, useMemo } from 'react';
import { BsCheck } from 'react-icons/bs';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import styles from './infos-worksite.module.scss';

import {
  ErrorField,
  InputText,
  StyledSelect,
  Textarea,
} from '~/lib/HooksFormFields';
import { useGetWorksitesList, useUpdateWorksite } from '~/hooks/worksites';
import { IWorksite } from '~/types/worksites';

const InfosForm = ({
  close,
  worksite,
} : {
  close: () => void,
  worksite?: IWorksite | null
}) => {
  const baselineContact = useMemo(() => ({
    firstName: (worksite?.contact?.firstName ?? '').trim(),
    lastName: (worksite?.contact?.lastName ?? '').trim(),
    phone: (worksite?.contact?.phone ?? '').trim(),
    email: (worksite?.contact?.email ?? '').trim(),
  }), [
    worksite?._id,
    worksite?.contact?.firstName,
    worksite?.contact?.lastName,
    worksite?.contact?.phone,
    worksite?.contact?.email,
  ]);

  const baselineAddress = useMemo(() => ({
    address: (worksite?.address?.address ?? '').trim(),
    address2: (worksite?.address?.address2 ?? '').trim(),
    city: (worksite?.address?.city ?? '').trim(),
    department: (worksite?.address?.department ?? '').trim(),
  }), [
    worksite?._id,
    worksite?.address?.address,
    worksite?.address?.address2,
    worksite?.address?.city,
    worksite?.address?.department,
  ]);

  const baselineAdditionalInformations = useMemo(() => ({
    truckType: (worksite?.additionalInformations?.truckType ?? '').trim(),
    truckMaxSize: (worksite?.additionalInformations?.truckMaxSize ?? '').trim(),
    materialUsed: (worksite?.additionalInformations?.materialUsed ?? '').trim(),
    load: (worksite?.additionalInformations?.load ?? '').trim(),
    stability: (worksite?.additionalInformations?.stability ?? '').trim(),
    access: (worksite?.additionalInformations?.access ?? '').trim(),
  }), [
    worksite?._id,
    worksite?.additionalInformations?.truckType,
    worksite?.additionalInformations?.truckMaxSize,
    worksite?.additionalInformations?.materialUsed,
    worksite?.additionalInformations?.load,
    worksite?.additionalInformations?.stability,
    worksite?.additionalInformations?.access,
  ]);

  const {
    control, handleSubmit, reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      _id: worksite?._id,
      description: worksite?.description,
      author: worksite?.author?._id,
      referrer: worksite?.referrer?._id,
      businessNumber: worksite?.businessNumber,
      estimatedDates: {
        startDate: worksite?.estimatedDates?.startDate ? format(new Date(worksite?.estimatedDates.startDate), 'yyyy-MM-dd') : null,
        endDate: worksite?.estimatedDates?.endDate ? format(new Date(worksite?.estimatedDates.endDate), 'yyyy-MM-dd') : null,
      },
      contact: {
        firstName: worksite?.contact?.firstName ?? '',
        lastName: worksite?.contact?.lastName ?? '',
        phone: worksite?.contact?.phone ?? '',
        email: worksite?.contact?.email ?? '',
      },
      address: {
        address: worksite?.address?.address ?? '',
        address2: worksite?.address?.address2 ?? '',
        city: worksite?.address?.city ?? '',
        department: worksite?.address?.department ?? '',
      },
      additionalInformations: {
        truckType: worksite?.additionalInformations?.truckType ?? '',
        truckMaxSize: worksite?.additionalInformations?.truckMaxSize ?? '',
        materialUsed: worksite?.additionalInformations?.materialUsed ?? '',
        load: worksite?.additionalInformations?.load ?? '',
        stability: worksite?.additionalInformations?.stability ?? '',
        access: worksite?.additionalInformations?.access ?? '',
      },
    },
  });

  useEffect(() => {
    if (!worksite?._id) return;
    reset({
      _id: worksite._id,
      description: worksite.description,
      author: worksite.author?._id,
      referrer: worksite.referrer?._id,
      businessNumber: worksite.businessNumber,
      estimatedDates: {
        startDate: worksite.estimatedDates?.startDate
          ? format(new Date(worksite.estimatedDates.startDate), 'yyyy-MM-dd') : null,
        endDate: worksite.estimatedDates?.endDate
          ? format(new Date(worksite.estimatedDates.endDate), 'yyyy-MM-dd') : null,
      },
      contact: {
        firstName: worksite.contact?.firstName ?? '',
        lastName: worksite.contact?.lastName ?? '',
        phone: worksite.contact?.phone ?? '',
        email: worksite.contact?.email ?? '',
      },
      address: {
        address: worksite.address?.address ?? '',
        address2: worksite.address?.address2 ?? '',
        city: worksite.address?.city ?? '',
        department: worksite.address?.department ?? '',
      },
      additionalInformations: {
        truckType: worksite.additionalInformations?.truckType ?? '',
        truckMaxSize: worksite.additionalInformations?.truckMaxSize ?? '',
        materialUsed: worksite.additionalInformations?.materialUsed ?? '',
        load: worksite.additionalInformations?.load ?? '',
        stability: worksite.additionalInformations?.stability ?? '',
        access: worksite.additionalInformations?.access ?? '',
      },
    });
  }, [worksite, reset]);

  const { data: list = {} } = useGetWorksitesList();

  const {
    isLoading,
    mutateAsync: updateWorksite,
  } = useUpdateWorksite();

  const {
    salesUsers = [],
  } = list;

  const submit = async (data: any) => {
    try {
      const nextContact = {
        firstName: (data.contact?.firstName ?? '').trim(),
        lastName: (data.contact?.lastName ?? '').trim(),
        phone: (data.contact?.phone ?? '').trim(),
        email: (data.contact?.email ?? '').trim(),
      };
      const contactChanged = (['firstName', 'lastName', 'phone', 'email'] as const).some(
        (k) => nextContact[k] !== baselineContact[k],
      );

      const nextAddress = {
        address: (data.address?.address ?? '').trim(),
        address2: (data.address?.address2 ?? '').trim(),
        city: (data.address?.city ?? '').trim(),
        department: (data.address?.department ?? '').trim(),
      };
      const addressChanged = (['address', 'address2', 'city', 'department'] as const).some(
        (k) => nextAddress[k] !== baselineAddress[k],
      );

      const nextAdditionalInformations = {
        truckType: (data.additionalInformations?.truckType ?? '').trim(),
        truckMaxSize: (data.additionalInformations?.truckMaxSize ?? '').trim(),
        materialUsed: (data.additionalInformations?.materialUsed ?? '').trim(),
        load: (data.additionalInformations?.load ?? '').trim(),
        stability: (data.additionalInformations?.stability ?? '').trim(),
        access: (data.additionalInformations?.access ?? '').trim(),
      };
      const additionalInformationsChanged = ([
        'truckType',
        'truckMaxSize',
        'materialUsed',
        'load',
        'stability',
        'access',
      ] as const).some(
        (k) => nextAdditionalInformations[k] !== baselineAdditionalInformations[k],
      );

      const payload: Record<string, unknown> = {
        _id: data._id,
        description: data.description,
        author: data.author,
        referrer: data.referrer,
        businessNumber: data.businessNumber,
        estimatedDates: data.estimatedDates,
      };
      if (contactChanged) {
        payload.contact = nextContact;
      }
      if (addressChanged) {
        payload.address = nextAddress;
      }
      if (additionalInformationsChanged) {
        payload.additionalInformations = nextAdditionalInformations;
      }
      await updateWorksite(payload as Partial<IWorksite>);
      close();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className={styles.form}>
      <h4>Informations générales</h4>
      <div className={styles.fields}>
        <div className={styles.containerField}>
          <Textarea
            name='description'
            label='Description du chantier'
            control={control}
          />
        </div>
        <h4>Contact chantier</h4>
        <p className={styles.contactHint}>
          Par défaut, les coordonnées viennent du devis.
          {' '}
          Modifiez-les ici pour les enregistrer sur le chantier.
        </p>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              name='contact.firstName'
              label='Prénom'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              name='contact.lastName'
              label='Nom'
              control={control}
            />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              name='contact.phone'
              label='Téléphone'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              name='contact.email'
              label='Mail'
              control={control}
            />
          </div>
        </div>

        <h4>Adresse</h4>
        <p className={styles.contactHint}>
          Par défaut, l’adresse vient du devis.
          {' '}
          Modifiez-la ici pour l’enregistrer sur le chantier.
        </p>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              name='address.address'
              label='Adresse'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              name='address.city'
              label='Ville'
              control={control}
            />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              name='address.department'
              label='Département'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              name='address.address2'
              label='Complément adresse'
              control={control}
            />
          </div>
        </div>

        <h4>Informations complémentaires</h4>
        <p className={styles.contactHint}>
          Par défaut, ces informations viennent du devis.
          {' '}
          Modifiez-les ici pour les enregistrer sur le chantier.
        </p>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              name='additionalInformations.truckType'
              label='Type de camion'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              name='additionalInformations.truckMaxSize'
              label='Taille maximal camion'
              control={control}
            />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              name='additionalInformations.materialUsed'
              label='Matériel utilisé'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              name='additionalInformations.load'
              label='Charge'
              control={control}
            />
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              name='additionalInformations.stability'
              label='Stabilité'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
            <InputText
              name='additionalInformations.access'
              label='Moyens Accès'
              control={control}
            />
          </div>
        </div>

        <div className={styles.containerField}>
          <InputText
            name='businessNumber'
            label='Numero d’affaire'
            control={control}
            rules={{
              required: 'Ce champs est requis',
            }}
          />
          {errors?.businessNumber?.message
            && <ErrorField message={errors?.businessNumber?.message} />}
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <StyledSelect
              name='author'
              label='Chargé d’affaire'
              control={control}
              options={salesUsers}
              isSearchable
            />
          </div>
          <div className={styles.containerField}>
            <StyledSelect
              name='referrer'
              label='Responsable chantier'
              control={control}
              options={salesUsers}
              rules={{
                required: 'Le responsable est requis',
              }}
              isSearchable
            />
            {errors.referrer?.message && <ErrorField message={errors.referrer?.message} />}
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              type="date"
              name='estimatedDates.startDate'
              label='Date démarrage prévisionnelle'
              control={control}
            />
          </div>
          <div className={styles.containerField}>
          <InputText
              type="date"
              name='estimatedDates.endDate'
              label='Date de fin prévisionnelle'
              control={control}
            />
          </div>
        </div>
      </div>
        <button className={styles.submit} disabled={isLoading} onClick={handleSubmit(submit)}>
          <span className='icon'>{BsCheck({})}</span>
          <span>Valider les informations</span>
        </button>
    </div>
  );
};

export default InfosForm;
